import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Calculator, User, Clock, DollarSign } from 'lucide-react';
import { usePayrollCalculator } from '@/hooks/usePayrollCalculations';
import { type EmployeePayrollData, type PayrollCalculationInput } from '@/utils/payrollCalculations';

interface PayrollCalculatorProps {
  employee?: EmployeePayrollData;
  onClose?: () => void;
}

export function PayrollCalculator({ employee, onClose }: PayrollCalculatorProps) {
  const { calculation, isCalculating, calculateSinglePayroll, resetCalculation } = usePayrollCalculator();
  
  const [formData, setFormData] = useState({
    hoursWorked: 0,
    vacationDaysTaken: 0,
    periodStart: new Date().toISOString().split('T')[0],
    periodEnd: new Date().toISOString().split('T')[0],
  });

  const handleCalculate = () => {
    if (!employee) return;

    const input: PayrollCalculationInput = {
      employee,
      hoursWorked: formData.hoursWorked,
      vacationDaysTaken: formData.vacationDaysTaken,
      periodStart: new Date(formData.periodStart),
      periodEnd: new Date(formData.periodEnd),
    };

    calculateSinglePayroll(input);
  };

  const handleReset = () => {
    resetCalculation();
    setFormData({
      hoursWorked: 0,
      vacationDaysTaken: 0,
      periodStart: new Date().toISOString().split('T')[0],
      periodEnd: new Date().toISOString().split('T')[0],
    });
  };

  if (!employee) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Payroll Calculator
          </CardTitle>
          <CardDescription>Select an employee to calculate their payroll</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Payroll Calculator
          </CardTitle>
          <CardDescription>Calculate payroll for {employee.name}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="periodStart">Period Start</Label>
              <Input
                id="periodStart"
                type="date"
                value={formData.periodStart}
                onChange={(e) => setFormData(prev => ({ ...prev, periodStart: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="periodEnd">Period End</Label>
              <Input
                id="periodEnd"
                type="date"
                value={formData.periodEnd}
                onChange={(e) => setFormData(prev => ({ ...prev, periodEnd: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hoursWorked">Hours Worked</Label>
              <Input
                id="hoursWorked"
                type="number"
                min="0"
                step="0.1"
                value={formData.hoursWorked}
                onChange={(e) => setFormData(prev => ({ ...prev, hoursWorked: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vacationDays">Vacation Days Taken</Label>
              <Input
                id="vacationDays"
                type="number"
                min="0"
                step="0.5"
                value={formData.vacationDaysTaken}
                onChange={(e) => setFormData(prev => ({ ...prev, vacationDaysTaken: parseFloat(e.target.value) || 0 }))}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleCalculate} disabled={isCalculating} className="flex-1">
              {isCalculating ? 'Calculating...' : 'Calculate Payroll'}
            </Button>
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
            {onClose && (
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {calculation && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Calculation Result
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  CHF {calculation.gross_salary.toFixed(2)}
                </div>
                <div className="text-sm text-muted-foreground">Gross Salary</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-destructive">
                  CHF {calculation.total_deductions.toFixed(2)}
                </div>
                <div className="text-sm text-muted-foreground">Total Deductions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  CHF {calculation.net_salary.toFixed(2)}
                </div>
                <div className="text-sm text-muted-foreground">Net Salary</div>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <h4 className="font-semibold">Deductions Breakdown</h4>
              <div className="space-y-2">
                {calculation.deductions.map((deduction, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{deduction.description}</span>
                      <Badge variant="outline" className="text-xs">
                        {deduction.rate_percent.toFixed(2)}%
                      </Badge>
                    </div>
                    <span className="font-medium">
                      CHF {deduction.deduction_amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}